// Path: ./src/app/pages/Tags/index.tsx
import { Avatar, Grid, Tooltip, Box } from "@mui/material";
import {
  usePromotionTags,
  useFlyerItemCategories,
  useMenuTagOptions,
  useStoreTagOptions,
  useProductTagOptions,
  useFigmaTemplateTagOptions,
  useImageTagOptions,
} from "../../../hooks";
import {
  CreateTagModal,
  EditFormType,
  TagTypes,
} from "../../components/CreateTagModal";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import {
  Container,
  ColorButton,
  FlyerTag,
  FlyerParentTag,
  MenuTag,
  ProductTag,
  PromotionTag,
  StoreTag,
  FigmaTemplateTag,
  ImageTag,
} from "./styled";
import { TagListColumn } from "./TagListColumn";
import { EditButtonWrapper } from "../../components/EditButtonTopRight";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useFlyerTags } from "../../../hooks/useFlyerTags";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { GfImage } from "@swagger/typescript-fetch-goflyer";

const Tags = () => {
  const { t } = useTranslation();
  const { promotionTags, fetchAdminPromotionTags } = usePromotionTags();

  const { flyerItemCategories, fetchFlyerItemCategories } =
    useFlyerItemCategories();
  const { menuTagOptionList, fetchMenuTags } = useMenuTagOptions();
  const { storeTagOptionList, fetchStoreTags } = useStoreTagOptions();
  const { productTagOptionList, fetchProductTags } = useProductTagOptions();
  const { figmaTemplateTagOptionList, fetchFigmaTemplateTags } =
    useFigmaTemplateTagOptions();
  const { imageTagOptionList, fetchImageTags } = useImageTagOptions();

  const [openTagModal, setOpenTagModal] = useState(false);
  const [editForm, setEditForm] = useState<EditFormType | undefined>();
  const { fetchFlyerTags, flyerTagsList } = useFlyerTags();
  const renderTagNameWithImage = (name: string, image?: GfImage) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {image && <Avatar src={image.src} sx={{ width: 24, height: 24 }} />}
      <span>{name}</span>
    </Box>
  );

  const closeModalAndCallFetchTagMethodByTagId = useCallback(
    async (tagId?: string) => {
      setEditForm(undefined);
      setOpenTagModal(false);
      const fetchTagMethodsByTagId: { [key: string]: () => Promise<void> } = {
        [`${TagTypes.Store}`]: () => fetchStoreTags(),
        [`${TagTypes.Flyer}`]: () => fetchFlyerTags(),
        [`${TagTypes.FlyerItem}`]: () => fetchFlyerItemCategories(),
        [`${TagTypes.Menu}`]: () => fetchMenuTags(),
        [`${TagTypes.Promotion}`]: () => fetchAdminPromotionTags(),
        [`${TagTypes.Product}`]: () => fetchProductTags(),
        [`${TagTypes.FigmaTemplate}`]: () => fetchFigmaTemplateTags(),
        [`${TagTypes.ImageTag}`]: () => fetchImageTags(),
      };
      if (tagId) {
        await fetchTagMethodsByTagId[tagId]();
      }
    },
    [],
  );

  const onEdit = (
    dto: {
      name: string;
      nameChinese?: string;
      id: string;
      description?: string;
      image?: GfImage;
      index?: number;
      visible?: boolean;
      isPreferenceOption?: boolean;
    },
    tagType: (typeof TagTypes)[keyof typeof TagTypes],
  ) => {
    setOpenTagModal(true);
    setEditForm({
      ...dto,
      name: dto.name,
      cnName: dto.nameChinese,
      id: dto.id,
      selectedTag: tagType,
      description: dto.description,
      image: dto.image,
      index: dto.index,
      visible: dto.visible,
      isPreferenceOption: dto.isPreferenceOption,
    });
  };
  return (
    <Container data-testid="tags-view-page">
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          style={{
            justifyContent: "start",
            display: "flex",
            marginTop: 12,
            paddingRight: 10,
          }}
        >
          <PermissionComponentWrapper
            requiredPermission={["create"]}
            requiredPermissionGroup={["tag"]}
            wrapperCss={`
                display:block;
                `}
          >
            <ColorButton
              variant="contained"
              onClick={() => setOpenTagModal(true)}
              data-testid="tags-create-button"
            >
              {t(messages.Create())}
            </ColorButton>
          </PermissionComponentWrapper>
        </Grid>
      </Grid>
      {openTagModal ? (
        <CreateTagModal
          onClose={closeModalAndCallFetchTagMethodByTagId}
          editForm={editForm}
        />
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 1,
          mt: "1rem",
        }}
      >
        <Box>
          <TagListColumn
            title={t(messages.Store_Tag())}
            count={storeTagOptionList?.length || 0}
            collapsible
            data-testid="tags-store-column"
          >
            {storeTagOptionList?.map((tag) => (
              <EditButtonWrapper
                key={tag.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: tag.name,
                      nameChinese: tag.nameChinese,
                      id: tag.id,
                      description: tag.description,
                      image: tag.image,
                    },
                    TagTypes.Store,
                  )
                }
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                data-testid={`tags-store-row-edit-${tag.id}`}
              >
                <Tooltip title={tag.name} placement="right" arrow>
                  <StoreTag>
                    {renderTagNameWithImage(tag.name, tag.image)}
                  </StoreTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Flyer_Tag())}
            count={flyerTagsList?.length || 0}
            collapsible
            data-testid="tags-flyer-column"
          >
            {flyerTagsList?.map((category) => (
              <EditButtonWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                key={category.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: category.name,
                      nameChinese: category.nameChinese,
                      id: category.id,
                      description: category.description,
                      image: category.image,
                      index: category.index,
                      visible: category.visible ?? undefined,
                    },
                    TagTypes.Flyer,
                  )
                }
                data-testid={`tags-flyer-row-edit-${category.id}`}
              >
                <Tooltip title={category.name} placement="right" arrow>
                  <FlyerParentTag>
                    {category.visible !== null &&
                      (category.visible ? (
                        <VisibilityIcon fontSize="small" color="success" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" />
                      ))}
                    <span className="flyer-index">#{category?.index}</span>
                    {category.image && (
                      <Avatar
                        src={category.image.src}
                        sx={{ width: 24, height: 24 }}
                      />
                    )}
                    <span className="flyer-name">{category.name}</span>
                  </FlyerParentTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Category())}
            count={flyerItemCategories.length}
            collapsible
            data-testid="tags-category-column"
          >
            {flyerItemCategories.map((category) => {
              return (
                <EditButtonWrapper
                  requiredPermission={["edit"]}
                  requiredPermissionGroup={["tag"]}
                  key={category.id}
                  onEdit={() =>
                    onEdit(
                      {
                        name: category.name,
                        nameChinese: category.nameChinese,
                        id: category.id,
                        description: category.description,
                        image: category.image,
                        isPreferenceOption: category.isPreferenceOption,
                      },
                      TagTypes.FlyerItem,
                    )
                  }
                  data-testid={`tags-category-row-edit-${category.id}`}
                >
                  <Tooltip
                    title={
                      category.isPreferenceOption
                        ? `${category.name} · ${t(
                            messages.preference_category_offered(),
                          )}`
                        : category.name
                    }
                    placement="right"
                    arrow
                  >
                    <FlyerTag>
                      {category.isPreferenceOption && (
                        <FavoriteBorderIcon
                          fontSize="small"
                          sx={{ color: "#f58220" }}
                          aria-label={t(messages.preference_category_offered())}
                        />
                      )}
                      {renderTagNameWithImage(category.name, category.image)}
                    </FlyerTag>
                  </Tooltip>
                </EditButtonWrapper>
              );
            })}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Menu_Tag())}
            count={menuTagOptionList?.length || 0}
            collapsible
            data-testid="tags-menu-column"
          >
            {menuTagOptionList?.map((menuTag) => (
              <EditButtonWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                key={menuTag.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: menuTag.name,
                      nameChinese: menuTag.nameChinese,
                      id: menuTag.id,
                      description: menuTag.description,
                      image: menuTag.image,
                    },
                    TagTypes.Menu,
                  )
                }
                data-testid={`tags-menu-row-edit-${menuTag.id}`}
              >
                <Tooltip title={menuTag.name} placement="right" arrow>
                  <MenuTag>
                    {renderTagNameWithImage(menuTag.name, menuTag.image)}
                  </MenuTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Promotion_Tag())}
            count={promotionTags?.length || 0}
            collapsible
            data-testid="tags-promotion-column"
          >
            {promotionTags?.map((tag) => (
              <EditButtonWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                key={tag.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: tag.name,
                      nameChinese: tag.nameChinese,
                      id: tag.id,
                      description: tag.description,
                      image: tag.image,
                    },
                    TagTypes.Promotion,
                  )
                }
                data-testid={`tags-promotion-row-edit-${tag.id}`}
              >
                <Tooltip title={tag.name} placement="right" arrow>
                  <PromotionTag key={tag.id}>
                    {renderTagNameWithImage(tag.name, tag.image)}
                  </PromotionTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Product_Tag())}
            count={productTagOptionList?.length || 0}
            collapsible
            data-testid="tags-product-column"
          >
            {productTagOptionList?.map((productTag) => (
              <EditButtonWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                key={productTag.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: productTag.name,
                      nameChinese: productTag.nameChinese,
                      id: productTag.id,
                      description: productTag.description,
                      image: productTag.image,
                    },
                    TagTypes.Product,
                  )
                }
                data-testid={`tags-product-row-edit-${productTag.id}`}
              >
                <Tooltip title={productTag.name} placement="right" arrow>
                  <ProductTag key={productTag.id}>
                    {renderTagNameWithImage(productTag.name, productTag.image)}
                  </ProductTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Figma_Template_Tag())}
            count={figmaTemplateTagOptionList?.length || 0}
            collapsible
            data-testid="tags-figma-template-column"
          >
            {figmaTemplateTagOptionList?.map((figmaTag) => (
              <EditButtonWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                key={figmaTag.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: figmaTag.name,
                      nameChinese: figmaTag.nameChinese,
                      id: figmaTag.id,
                      description: figmaTag.description,
                      image: figmaTag.image,
                    },
                    TagTypes.FigmaTemplate,
                  )
                }
                data-testid={`tags-figma-template-row-edit-${figmaTag.id}`}
              >
                <Tooltip title={figmaTag.name} placement="right" arrow>
                  <FigmaTemplateTag>
                    {renderTagNameWithImage(figmaTag.name, figmaTag.image)}
                  </FigmaTemplateTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
        <Box>
          <TagListColumn
            title={t(messages.Image_Tag())}
            count={imageTagOptionList?.length || 0}
            collapsible
            data-testid="tags-image-column"
          >
            {imageTagOptionList?.map((imageTag) => (
              <EditButtonWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["tag"]}
                key={imageTag.id}
                onEdit={() =>
                  onEdit(
                    {
                      name: imageTag.name,
                      nameChinese: imageTag.nameChinese,
                      id: imageTag.id,
                      description: imageTag.description,
                      image: imageTag.image,
                    },
                    TagTypes.ImageTag,
                  )
                }
                data-testid={`tags-image-row-edit-${imageTag.id}`}
              >
                <Tooltip title={imageTag.name} placement="right" arrow>
                  <ImageTag>
                    {renderTagNameWithImage(imageTag.name, imageTag.image)}
                  </ImageTag>
                </Tooltip>
              </EditButtonWrapper>
            ))}
          </TagListColumn>
        </Box>
      </Box>
    </Container>
  );
};
export default Tags;
